import { Button, Card, Typography } from '@repo/ui';
import { ButtonColors, ButtonSizes } from 'components';
import { Fragment, useMemo } from 'react';
import type { SingleConditionMetric } from 'services/conditions/useConditionMetrics';
import styles from './ConditionAssociationsCard.module.css';
import { ConditionFlagsSection } from './ConditionFlagsSection';
import { ConditionMetricRow } from './ConditionMetricRow';
import {
  FLAGS_NOT_PRESENT_LABEL,
  FLAGS_PRESENT_LABEL,
  getAssociationsTitle,
  NO_FLAGS_MARKED_HIDE_CONSULTS_SUBTEXT,
  NO_FLAGS_MARKED_SUBTEXT,
  NO_FLAGS_SUBTEXT,
  NO_FLAGS_TITLE,
  NO_NON_FLAGGED_TEXT,
  SCHEDULE_CALL_LABEL,
} from './utils/conditionDetailCopy';
import {
  type ConditionSectionGroup,
  groupConditionMetrics,
} from './utils/groupConditionMetrics';

const renderGroups = (groups: ConditionSectionGroup[]) =>
  groups.map((group, index) => (
    <Fragment key={group.section || index}>
      {index > 0 && <hr className={styles.groupSeparator} />}
      <div className={styles.group}>
        {group.section && (
          <Typography variant='label' className={styles.categoryTitle}>
            {group.section}
          </Typography>
        )}
        {group.metrics.map(metric => (
          <ConditionMetricRow key={metric.metric_id} metric={metric} />
        ))}
      </div>
    </Fragment>
  ));

export const ConditionAssociationsCard = ({
  metrics,
  samplingType,
  markedInSurvey = false,
  hideConsults = false,
  onScheduleCall,
  onNonFlaggedExpand,
}: {
  metrics: SingleConditionMetric[];
  samplingType?: string;
  markedInSurvey?: boolean;
  hideConsults?: boolean;
  onScheduleCall?: () => void;
  onNonFlaggedExpand?: () => void;
}) => {
  const { flaggedMetrics, nonFlaggedMetrics } = useMemo(
    () => groupConditionMetrics(metrics),
    [metrics],
  );

  const flaggedCount = flaggedMetrics.reduce(
    (sum, g) => sum + g.metrics.length,
    0,
  );
  const nonFlaggedCount = nonFlaggedMetrics.reduce(
    (sum, g) => sum + g.metrics.length,
    0,
  );

  const noFlagsSubtext = markedInSurvey
    ? hideConsults
      ? NO_FLAGS_MARKED_HIDE_CONSULTS_SUBTEXT
      : NO_FLAGS_MARKED_SUBTEXT
    : NO_FLAGS_SUBTEXT;
  const showScheduleCall = markedInSurvey && !hideConsults && !!onScheduleCall;

  return (
    <Card variant='wrapper'>
      <div className={styles.body}>
        <Typography variant='heading-m'>
          {getAssociationsTitle(samplingType)}
        </Typography>

        <ConditionFlagsSection
          label={FLAGS_PRESENT_LABEL}
          count={flaggedCount}
          defaultExpanded
        >
          {flaggedCount > 0 ? (
            renderGroups(flaggedMetrics)
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateCopies}>
                <Typography variant='body-s' className={styles.emptyTitle}>
                  {NO_FLAGS_TITLE}
                </Typography>
                <Typography variant='caption' className={styles.emptySubtext}>
                  {noFlagsSubtext}
                </Typography>
              </div>
              {showScheduleCall && (
                <Button
                  label={SCHEDULE_CALL_LABEL}
                  color={ButtonColors.PURPLE}
                  size={ButtonSizes.S}
                  onClick={onScheduleCall}
                />
              )}
            </div>
          )}
        </ConditionFlagsSection>

        <ConditionFlagsSection
          label={FLAGS_NOT_PRESENT_LABEL}
          count={nonFlaggedCount}
          defaultExpanded={nonFlaggedCount === 0}
          onExpand={onNonFlaggedExpand}
        >
          {nonFlaggedCount > 0 ? (
            renderGroups(nonFlaggedMetrics)
          ) : (
            <div className={styles.emptyState}>
              <Typography variant='body-s' className={styles.emptyTitle}>
                {NO_NON_FLAGGED_TEXT}
              </Typography>
            </div>
          )}
        </ConditionFlagsSection>
      </div>
    </Card>
  );
};
